import { PriceOverrideBreakdownItem } from "@api/graphql/generated/generated-types";

/**
 * Normalizes price breakdown items by ensuring all items have a valid quantity.
 * Sets quantity to 1 for backwards compatibility when quantity is undefined or null.
 *
 * @param items - Array of price breakdown items to normalize
 * @returns Normalized array with quantity set to 1 where missing
 */
export function normalizePriceBreakdownItems(
  items: PriceOverrideBreakdownItem[] | undefined | null
): PriceOverrideBreakdownItem[] {
  if (!items) {
    return [];
  }

  return items.map((item) =>
    item.quantity === undefined || item.quantity === null
      ? {
          ...item,
          quantity: 1,
        }
      : item
  );
}

/**
 * Calculates the total sum of normalized price breakdown items.
 * Uses quantity of 1 as fallback for backwards compatibility.
 *
 * @param items - Array of price breakdown items
 * @returns Total sum of all items (amount * quantity)
 */
export function calculatePriceBreakdownTotal(
  items: PriceOverrideBreakdownItem[]
): number {
  return items.reduce(
    (acc, item) => acc + Number(item.amount) * (item.quantity || 1),
    0
  );
}

/**
 * Validates that breakdown items total matches the expected price override.
 * Uses toFixed(2) comparison to avoid floating point precision issues.
 *
 * @param items - Array of price breakdown items
 * @param expectedTotal - Expected total price
 * @returns True if totals match, false otherwise
 */
export function doesPriceBreakdownMatchExpectedTotal(
  items: PriceOverrideBreakdownItem[],
  expectedTotal: number
): boolean {
  const calculatedTotal = calculatePriceBreakdownTotal(items);
  return calculatedTotal.toFixed(2) === expectedTotal.toFixed(2);
}

/**
 * Checks if all price breakdown items have a quantity.
 *
 * @param items - Array of price breakdown items
 * @returns True if all items have a quantity, false otherwise
 */
export function isQuantityAlwaysSpecifiedInPriceBreakdown(
  items: PriceOverrideBreakdownItem[]
): boolean {
  return items.every(
    (item) => item.quantity !== undefined && item.quantity !== null
  );
}
